import './debug.css';
import {
  CompartmentComponent,
  CoreApi,
  DebugSystem,
  ShoppingBagComponent,
  UNAVAILABLE_PRODUCTS_KEY
} from '@inter-ikea-storageone/range';
import * as dat from 'dat.gui';
import {
  ConnectorVisualizationSystem,
  ColliderVisualizationSystem,
  CoreDebug,
  DefaultVisualizer,
  EntityConnectorVisualizationSystem,
  EntityColliderVisualizationSystem,
  FunctionalMarginVisualizationSystem
} from '@inter-ikea-core/core';
import { PlannerConfig } from 'rangeConfig/@types';
import {
  ProductComponent,
  ComponentIds,
  SelectableComponent,
  SelectedComponent,
  DisplayNameComponent,
  OutlineableComponent,
  HierarchyUtils,
  ComponentUtils,
  ProductUtils
} from '@inter-ikea-core/core';
import { plannerRenderSettings } from 'config/plannerRenderSettings';
import { CeilingLamp } from '@inter-ikea-betrakta/viewer/dist/modules/CeilingLamp';
import { addExportGlbToMenu } from './DebugExportGlb';
import { AppDispatch } from 'ducks/configureStore';
import { setShowScreensaver } from 'ducks/layout';
import * as allOfCore from '@inter-ikea-core/core';

export const PRINT_PRODUCTS_KEY = 'S1Debug/PrintProducts';

const help = `
# Temporarily set products as unavailable during this browser session
PlannerDebug.setUnavailableProducts()

# Reset ignored articles
PlannerDebug.reset()

# Download the scene state as a file
PlannerDebug.downloadScene()

# Load Product Catalog
PlannerDebug.loadProductCatalog(version)

# Toggle Debug UI
PlannerDebug.toggleDebugUI()

# Get all entities in the scene
PlannerDebug.getEntitiesInScene()

# Get all planner product entities in the scene
PlannerDebug.getProductEntitiesInScene()

# Get all shopping bag entities in the scene
PlannerDebug.getShoppingBagEntitiesInScene()

# Get world and canvas position for the product entities in the scene
PlannerDebug.getProductEntitiesScenePosition()

# Get what components the selected entity has
PlannerDebug.getComponentsForSelectedEntity()

# Get selected entity product reference
PlannerDebug.getSelectedEntityProductRef()

# Get selected entity product reference
PlannerDebug.getSystems()

# Trigger planner screensaver
PlannerDebug.showScreensaver()

# Hide planner screensaver
PlannerDebug.hideScreensaver()

# Adds SelectedComponent to entity in scene with given product id
PlannerDebug.selectProductInScene()

# Makes all seperate articles selectable
PlannerDebug.makeAllArticlesSelectable()

# Removes orbit and zoom limits
PlannerDebug.freeCamera()

# Get IPEX viewer
# API: https://bitbucket.org/i-p-e-x/viewer/src/master/src/api.ts
PlannerDebug.getViewer()

# Set products to print product information about
PlannerDebug.printProductData()

# Print this help again
PlannerDebug.help()
`;

class PlannerDebug {
  coreApi: CoreApi;
  dispatch: AppDispatch;
  plannerConfig: PlannerConfig;
  onReloadProductCatalog: () => void;
  gui: dat.GUI;
  ceilingLamp: CeilingLamp | null;

  state = {
    connectorVisualization: false,
    colliderVisualization: false,
    updateEntityVisualization: () => {},
    colliderToggle: false,
    babylonJsDebug: false,
    functionalMarginsToggle: false
  };

  treeVisualizationFolder: null | dat.GUI = null;

  constructor(
    coreApi: CoreApi,
    dispatch: AppDispatch,
    plannerConfig: PlannerConfig,
    onReloadProductCatalog: () => void
  ) {
    this.coreApi = coreApi;
    this.onReloadProductCatalog = onReloadProductCatalog;
    this.plannerConfig = plannerConfig;
    this.dispatch = dispatch;
    this.gui = this.configureDebugUI();
    this.help();
    this.installFileDrop();
    this.ceilingLamp = null;
  }

  setUnavailableProducts(...products: string[]) {
    sessionStorage.setItem(UNAVAILABLE_PRODUCTS_KEY, JSON.stringify(products));
  }

  printProductData(...products: string[]) {
    sessionStorage.setItem(PRINT_PRODUCTS_KEY, JSON.stringify(products));
  }

  reset() {
    sessionStorage.removeItem(UNAVAILABLE_PRODUCTS_KEY);
    this.coreApi.loadProductCatalogs(this.plannerConfig.productCatalogs);
  }

  showScreensaver() {
    this.dispatch(setShowScreensaver(true));
  }

  hideScreensaver() {
    this.dispatch(setShowScreensaver(false));
  }

  installFileDrop() {
    // Setup listeners for drag and drop state file
    let timeoutId: NodeJS.Timeout;
    document.body.addEventListener('dragover', event => {
      event.preventDefault();
      event.stopPropagation();

      if (!event.dataTransfer) {
        return;
      }

      event.dataTransfer.effectAllowed = 'copy';
      event.dataTransfer.dropEffect = 'copy';

      const file = event.dataTransfer.files[0] || event.dataTransfer.items[0];

      // if file type is not text/plain we do nothing
      if (
        !event.dataTransfer.types.includes('Files') ||
        (file?.type && file?.type !== 'application/json')
      ) {
        return;
      }

      document.body.classList.add('debug-file-drag-active');

      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      timeoutId = setTimeout(() => {
        document.body.classList.remove('debug-file-drag-active');
      }, 500);
    });

    document.body.addEventListener('drop', async event => {
      if (!event.dataTransfer) return;

      event.preventDefault();

      const file: any = event.dataTransfer?.files[0] || event.dataTransfer?.items[0];

      // if file type is not text/plain we do nothing
      if (file?.type !== 'application/json') return;

      const reader = new FileReader();

      reader.addEventListener('load', readEvent => {
        try {
          const content = JSON.parse(readEvent?.target?.result as string);
          switch (file.name) {
            case 'template.json':
              this.loadProductTemplate(content);
              break;
            case 'catalog.json':
              this.loadProductCatalog(content);
              break;
            default:
              break;
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('unable to load scene from file', e);
        }
      });

      if (file.kind === 'string') return;

      reader.readAsText(file);
    });
  }

  configureDebugUI(): dat.GUI {
    const gui = new dat.GUI({ closeOnTop: true, width: 270 });
    gui.domElement.style.position = 'absolute';
    gui.domElement.style.display = 'none';
    gui.domElement.style.top = '6rem';
    gui.domElement.style.left = '1rem';

    const coreServices = this.coreApi.getServices();
    const connectorVisualizationSystem = new ConnectorVisualizationSystem(coreServices);
    const colliderVisualizationSystem = new ColliderVisualizationSystem(coreServices);
    const functionalMarginVisualizationSystem = new FunctionalMarginVisualizationSystem(
      coreServices
    );

    gui
      .add(this.state, 'connectorVisualization')
      .name('Show Connectors')
      .onFinishChange(val => {
        if (val) {
          connectorVisualizationSystem.enable();
        } else {
          connectorVisualizationSystem.disable();
        }
      });
    gui
      .add(this.state, 'colliderVisualization')
      .name('Show Colliders')
      .onFinishChange(val => {
        if (val) {
          colliderVisualizationSystem.enable();
        } else {
          colliderVisualizationSystem.disable();
        }
      });
    gui.add(this.state, 'babylonJsDebug').onFinishChange(val => {
      const viewer = this.coreApi.getModules().viewer();
      viewer?.debug(val);
    });

    gui
      .add(this.state, 'colliderToggle')
      .name('Disable Collisions')
      .onFinishChange(val => {
        if (val) {
          this.coreApi
            .getServices()
            // @ts-ignore
            .validations.items?.toArray()
            .find((item: any) => item.name === 'Collision')
            .disable() as any;
        } else {
          this.coreApi
            .getServices()
            // @ts-ignore
            .validations.items?.toArray()
            .find((item: any) => item.name === 'Collision')
            .enable() as any;
        }
      });

    gui
      .add(this.state, 'functionalMarginsToggle')
      .name('Show Functional margins')
      .onFinishChange(val => {
        if (val) {
          functionalMarginVisualizationSystem.enable();
        } else {
          functionalMarginVisualizationSystem.disable();
        }
      });

    gui
      .add(this.state, 'updateEntityVisualization')
      .name('Update Entities')
      .onChange(() => {
        const entities = this.getEntitiesInScene();

        if (this.treeVisualizationFolder) {
          if (Object.keys(this.treeVisualizationFolder.__folders).length > 0) {
            Object.keys(this.treeVisualizationFolder.__folders).forEach((key: any) => {
              this.treeVisualizationFolder?.removeFolder(
                this.treeVisualizationFolder.__folders[key]
              );
            });
          }

          entities.forEach(entity => {
            const displayName = entity.getComponent<DisplayNameComponent>(
              ComponentIds.DISPLAY_NAME_COMPONENT_ID
            )?.name;

            const entityData = {
              name: displayName || '',
              logEntity: () => {
                // eslint-disable-next-line no-console
                console.log(entity);
              }
            };
            const entityFolder = this.treeVisualizationFolder?.addFolder(entity.id.toString());

            entityFolder?.add(entity, 'id').name('ID');
            if (entityFolder?.add(entityData, 'name')) {
              entityFolder?.add(entityData, 'name').name('Name');
            }
            entityFolder?.add(entityData, 'logEntity').name('Console log');
          });
        }
      });

    const renderVisualizationFolder = () => {
      this.treeVisualizationFolder = gui.addFolder('Entity Visualization');
    };

    renderVisualizationFolder();

    const roomRenderingFolder = gui.addFolder('Render Settings');

    const environmentMapFolder = roomRenderingFolder.addFolder('Environment Map');

    environmentMapFolder
      .add(plannerRenderSettings.environmentMap, 'url')
      .name('Env Map Path')
      .onFinishChange(val => {
        this.coreApi.ipexViewer.set_environment_map(val, 'env');
      });

    environmentMapFolder
      .add(plannerRenderSettings.environmentMap, 'intensity', 0, 2)
      .name('Env Intensity')
      .onFinishChange(val => {
        this.coreApi.ipexViewer.set_environment_map_intensity(val);
      });

    addExportGlbToMenu(gui, this.coreApi);

    return gui;
  }

  toggleDebugUI() {
    const coreServices = this.coreApi.getServices();
    const visualizer = new DefaultVisualizer(coreServices);
    const entityConnectorVisualizationSystem = new EntityConnectorVisualizationSystem(
      this.coreApi.getServices(),
      visualizer
    );
    entityConnectorVisualizationSystem.enable();
    const entityColliderVisualizationSystem = new EntityColliderVisualizationSystem(
      this.coreApi.getServices(),
      visualizer
    );
    entityColliderVisualizationSystem.enable();
    const display = this.gui.domElement.style.display;
    this.gui.domElement.style.display = display === 'none' ? 'block' : 'none';
    const parent = this.gui.domElement.parentElement;
    if (parent) {
      parent.style.zIndex = '999'; //Modal has 499 so debug window needs to be higher
    }
  }

  getEntitiesInScene() {
    const scene = this.coreApi.core.services.sceneManager;
    return scene.entities;
  }

  listEntitiesInScene() {
    const entitiesInScene = this.getEntitiesInScene();
    return entitiesInScene.map(
      entity => ProductUtils.productRef(entity) || ComponentUtils.displayName(entity)
    );
  }

  getShoppingBagEntitiesInScene() {
    return this.coreApi.entities.getAllShoppingBagEntities();
  }

  getProductEntitiesInScene() {
    return this.coreApi.entities.getAllProductEntities();
  }

  selectProductInScene(coreProductId: string) {
    const entities = this.getEntitiesInScene();
    const entityToSelect = entities.find(
      entity =>
        entity.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)?.productRef ===
        coreProductId
    );
    if (entityToSelect) {
      entityToSelect.addComponent(new SelectedComponent());
    }
  }

  getProductEntitiesScenePosition() {
    return this.coreApi.entities.getAllWithPosition();
  }

  makeAllArticlesSelectable() {
    const entitiesInScene = this.coreApi.entities.getByComponent(ShoppingBagComponent.id);
    entitiesInScene.forEach(entity => {
      entity.addComponent(new SelectableComponent());
      entity.addComponent(new OutlineableComponent());
    });
  }

  getViewer() {
    return this.coreApi.ipexViewer;
  }

  getComponentsForSelectedEntity() {
    return this.coreApi.entities.findSelectedEntity()?.components();
  }

  getSelectedEntityProductRef() {
    let selectedEntity = this.coreApi.entities.findSelectedEntity();
    if (selectedEntity?.hasComponent(CompartmentComponent.id)) {
      selectedEntity = HierarchyUtils.findWithComponentInUpwardsHierarchy(
        selectedEntity,
        ComponentIds.CONNECTION_ROOT_COMPONENT
      );
    }
    return (
      selectedEntity?.getComponent<ProductComponent>(ComponentIds.PRODUCT_COMPONENT_ID)
        ?.productRef || ''
    );
  }

  updateRoom() {
    const kvadratRoom = this.coreApi.kvadratRoom;
    if (!kvadratRoom) return;

    const roomEntity = this.coreApi
      .getServices()
      .sceneManager.entities.find(entity =>
        entity.hasComponent(ComponentIds.ROOM_GEOMETRY_COMPONENT_ID)
      );
    const dimensions = this.coreApi.kvadratRoom?.getDimensions();

    if (roomEntity && dimensions) {
      kvadratRoom.setRoomSize(dimensions.x, dimensions.y, dimensions.z);
    }
  }

  async loadProductTemplate(template: any) {
    const catalogToReplace = this.plannerConfig.productCatalogs.find(
      c => c.name === template.range
    );
    if (catalogToReplace) {
      await this.coreApi.loadCatalog({
        name: catalogToReplace.name,
        catalog: catalogToReplace.catalog,
        template,
        assetsPath: catalogToReplace.assetsPath
      });
      this.replaceAll();
    }
  }

  async loadProductCatalog(catalog: any) {
    const catalogToReplace = this.plannerConfig.productCatalogs.find(c => c.name === catalog.range);
    if (catalogToReplace) {
      await this.coreApi.loadCatalog({
        name: catalogToReplace.name,
        catalog,
        template: catalogToReplace.template,
        assetsPath: catalogToReplace.assetsPath
      });
      this.onReloadProductCatalog();
      this.replaceAll();
    }
  }

  replaceAll() {
    this.coreApi.entities.getAll().forEach(entity => {
      const productRef = entity.getComponentUnsafe<ProductComponent>(
        ComponentIds.PRODUCT_COMPONENT_ID
      )?.productRef;
      this.coreApi.products.replace(entity.id, productRef);
    });
  }

  getSystems() {
    return this.coreApi.getServices().entityManager.getSystemsRegistered();
  }

  debugClicker() {
    this.coreApi.registerSystem(new DebugSystem(this.coreApi));
  }

  help() {
    // eslint-disable-next-line no-console
    console.log(
      `%cDEBUG`,
      'color: #4595FF; font-size: 42px; font-weight: bold; text-shadow: 2px 2px 2px #0058A3; font-family: monospace;'
    );
    // eslint-disable-next-line no-console
    console.log(
      `%c${help}`,
      'color: #4595FF; font-family: monospace; font-size: 13px; font-weight: bold'
    );
  }
}

export function installDebug(
  coreApi: CoreApi,
  dispatch: AppDispatch,
  plannerConfig: PlannerConfig,
  onReloadProductCatalog: () => void
) {
  // @ts-ignore
  window.PlannerDebug = new PlannerDebug(coreApi, dispatch, plannerConfig, onReloadProductCatalog);
  const coreDebug = new CoreDebug(coreApi.core);
  coreDebug.setAllOfCore(allOfCore);
  // @ts-ignore
  window.CoreDebug = coreDebug;
  // @ts-ignore
  if (window.__IPEX_DEVTOOLS__) {
    // @ts-ignore
    window.__IPEX_DEVTOOLS__.setCore(coreApi.core);
    // @ts-ignore
    window.__IPEX_DEVTOOLS__.setCoreDebug(window.CoreDebug);
  }
}
